import { createSelector } from "@reduxjs/toolkit";
import { User } from "../interfaces/user";
import { createDBServiceSlice } from "./db-service-reducers";
import { RootState } from "./store";
import { apiKeysSelectByQuery } from "./api-keys-service-reducer";
import { Campaign } from "./api-interfaces";

export const {
    slice: UserSlice,
    dbThunks: _UserThunks,
    selectByQuery: _userSelectByQuery,
    selectOneObjectByQuery: _userSelectOneObjectByQuery,
    selectOneFieldByQuery: _userSelectOneFieldByQuery,
    selectors: _userSelectors,
    selectByQueryOnlyLength: _userSelectByQueryOnlyLength,
    selectByQueryOnlyMaxMin: _userSelectByQueryOnlyMaxMin,
} = createDBServiceSlice<User>({
    service: "users",
    initialState: { ids: [], entities: {}, itemsLoaded: false, itemsLoading: false },
    getReducerState: (state) => state.DBUserReducer,
});
export const DBUserReducer = UserSlice.reducer;
export const UserReducerActions = UserSlice.actions;
export type userReducerActions = keyof typeof UserReducerActions;
export const DBUserThunks = _UserThunks;
export const userSelectors = _userSelectors;
export const userSelectByQuery = _userSelectByQuery;
export const userSelectByQueryOnlyLength = _userSelectByQueryOnlyLength;
export const userSelectByQueryOnlyMaxMin = _userSelectByQueryOnlyMaxMin;
export const userSelectOneObjectByQuery = _userSelectOneObjectByQuery;
export const userSelectOneFieldByQuery = _userSelectOneFieldByQuery;

// Assuming you have basic selectors to get users and API keys:
const selectAllUsers = (state: RootState) => userSelectByQuery(state.DBUserReducer, {});
const selectApiKeys = (state: RootState) => apiKeysSelectByQuery(state.DBApiKeysReducer, {});

// Create a selector that combines users with their API keys
export const selectUsersWithApiKeys = createSelector(
    [selectAllUsers, selectApiKeys],
    (users, apiKeys) => {
        return Object.values(users)
            .filter(user => user.type === "publisher")
            .map(user => {
                const userApiKeys = apiKeys.filter(key => key.owner === user._id)
                    .map(key => key.key)
                    .join(", ");
                return { ...user, apiKeys: userApiKeys };
            });
    }
);

// Selector to get campaign data from RTK Query cache
const selectCampaignData = (state: RootState) => {
    // Find the campaign query in the RTK Query cache
    const campaignQuery = Object.values(state.api.queries).find(
        (query: any) => query?.originalArgs?.endpoint === 'campaign' && query?.status === 'fulfilled'
    ) as any;
    return campaignQuery?.data?.campaigns || [];
};

// Enhanced selector that includes campaign data for better search and display
export const selectUsersWithApiKeysAndCampaigns = createSelector(
    [selectAllUsers, selectApiKeys, selectCampaignData],
    (users, apiKeys, campaignData: Campaign[]) => {
        return Object.values(users)
            .filter((user): user is User => user.type === "publisher")
            .map(user => {
                const userApiKeys = apiKeys.filter((key: any) => key.owner === user._id)
                    .map((key: any) => key.key)
                    .join(", ");

                // Get campaign details for this user using the cached campaign data
                const userCampaigns = user.campaignList?.map(campaignId => {
                    const campaign = campaignData.find((c: Campaign) => c.id === campaignId);
                    return {
                        id: campaignId,
                        name: campaign?.name || campaignId,
                        platform: campaign?.platform || 'voluum'
                    };
                }) || [];

                // Create searchable campaign names string
                const campaignNames = userCampaigns.map(c => c.name).join(' ');

                return { 
                    ...user, 
                    apiKeys: userApiKeys,
                    userCampaigns,
                    campaignNames // For search purposes
                };
            });
    }
);